import type { SessionUser } from '@motoveo/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { formatCents, formatDate } from '../lib/format';

const pct = (x: number | null): string => (x == null ? '—' : `${Math.round(x * 100)}%`);

/**
 * The dealer-recruitment cockpit: due follow-ups + the pipeline board + all LIVE ISOs grouped by
 * demand cluster (with each dealer's buyer-in-hand flag, reachability, and response history).
 */
export function CockpitPage({ user }: { user: SessionUser }) {
  const pipeline = useQuery({ queryKey: ['cockpit', 'pipeline'], queryFn: api.cockpit.pipeline });
  const active = useQuery({ queryKey: ['cockpit', 'active-isos'], queryFn: api.cockpit.activeIsos });

  return (
    <Layout user={user}>
      <div className="row-between">
        <h2>Recruitment cockpit</h2>
        <Link to="/dealers/import" className="nav-link">
          Import dealer CSV →
        </Link>
      </div>

      {/* --- due follow-up reminders --- */}
      <div className="card">
        <h3>Due follow-ups</h3>
        {pipeline.isLoading ? (
          <p className="muted">Loading…</p>
        ) : pipeline.data && pipeline.data.due_reminders.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Due</th>
                  <th>Dealer</th>
                  <th>Stage</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {pipeline.data.due_reminders.map((r) => (
                  <tr key={r.interaction_id}>
                    <td className={r.overdue ? 'error' : 'muted'}>
                      {r.overdue ? '⚠ ' : ''}
                      {formatDate(r.follow_up_at)}
                    </td>
                    <td>
                      <Link to={`/dealers/${r.dealer_id}`}>{r.dealer_business_name}</Link>
                    </td>
                    <td>
                      <span className="pill">{r.pipeline_stage}</span>
                    </td>
                    <td>{r.follow_up_note ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">No follow-ups due.</p>
        )}
      </div>

      {/* --- pipeline board --- */}
      <h3>Pipeline board</h3>
      {pipeline.isError ? (
        <p className="error">{(pipeline.error as Error).message}</p>
      ) : pipeline.data ? (
        <div className="pipeline-board">
          {pipeline.data.stages.map((col) => (
            <div key={col.stage} className="card pipeline-col">
              <div className="row-between">
                <strong>{col.stage}</strong>
                <span className="pill">{col.count}</span>
              </div>
              <ul className="plain">
                {col.dealers.map((d) => (
                  <li key={d.id}>
                    <Link to={`/dealers/${d.id}`}>{d.business_name}</Link>{' '}
                    <span className="muted">{d.state}</span>
                    {d.has_named_buyer ? <span className="pill conf-high">buyer</span> : null}
                    {d.active_iso_pref_count > 0 ? <span className="muted"> · {d.active_iso_pref_count} ISO</span> : null}
                  </li>
                ))}
                {col.dealers.length === 0 ? <li className="muted">—</li> : null}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {/* --- active ISOs by demand cluster --- */}
      <div className="row-between">
        <h3>Live ISOs by demand cluster</h3>
        {active.data ? (
          <span className="muted">
            {active.data.total_active_isos} live · {active.data.named_buyer_isos} with buyer-in-hand
          </span>
        ) : null}
      </div>
      {active.isLoading ? (
        <p className="muted">Loading…</p>
      ) : active.isError ? (
        <p className="error">{(active.error as Error).message}</p>
      ) : active.data && active.data.groups.length > 0 ? (
        active.data.groups.map((g) => (
          <div key={g.key} className="card">
            <div className="row-between">
              <h4>
                {g.key} <span className="pill">{g.count}</span>
              </h4>
              {g.named_buyer_count > 0 ? <span className="pill conf-high">{g.named_buyer_count} buyer-in-hand</span> : null}
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Dealer</th>
                    <th>State</th>
                    <th>Stage</th>
                    <th>ISO</th>
                    <th>Trans</th>
                    <th>Budget ≤</th>
                    <th>Buyer</th>
                    <th>Reach</th>
                    <th>Resp rate</th>
                    <th>Bids</th>
                    <th>Last resp</th>
                  </tr>
                </thead>
                <tbody>
                  {g.entries.map((e) => (
                    <tr key={e.iso_pref_id}>
                      <td>
                        <Link to={`/dealers/${e.dealer_id}`}>{e.dealer_business_name}</Link>
                      </td>
                      <td className="muted">{e.dealer_state}</td>
                      <td>
                        <span className="pill">{e.pipeline_stage}</span>
                      </td>
                      <td>{e.label}</td>
                      <td className="muted">{e.transmission.length ? e.transmission.join('/') : 'any'}</td>
                      <td>{formatCents(e.budget_max_cents)}</td>
                      <td>{e.has_named_buyer ? <span className="pill conf-high">buyer</span> : '—'}</td>
                      <td>{e.reachable ? <span className="ok-dot">●</span> : <span className="warn">—</span>}</td>
                      <td>{e.response_stats.asked > 0 ? pct(e.response_stats.response_rate) : '—'}</td>
                      <td>{e.response_stats.bids}</td>
                      <td className="muted">{formatDate(e.response_stats.last_responded_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      ) : (
        <p className="muted">No live ISOs yet. Capture one from a dealer’s page after a call.</p>
      )}
    </Layout>
  );
}
